import { useEffect, useRef, useState } from 'react';
import { formatBoardNumber } from '../../projectBoardUtils';

/**
 * Formatter-bound numeric text input (`fn.uni_tools.bindFormatter` /
 * FormatterBinder — §3.9 "Binds bindFormatter to all input[type=text] …",
 * parity 85): comma-grouped display (integer 0dp / decimal 2dp), `.invalid`
 * while the typed value is out of [data-min, data-max], commit-on-change
 * (blur) with the DECODED numeric value (getValue — commas stripped, NOT
 * clamped; the server recomputes/validates).
 */
export type SalesFormatterType = 'integer' | 'decimal';

interface SalesNumberInputProps {
  name: string;
  value: number | string | null | undefined;
  formatter: SalesFormatterType;
  className: string;
  min?: number;
  max?: number;
  step?: string;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onCommit?: (name: string, value: number | null) => void;
}

const display = (value: number | string | null | undefined, kind: SalesFormatterType): string => {
  if (value === null || value === undefined || value === '') return '';
  return formatBoardNumber(value, kind);
};

/** decode(): strip grouping; '' → null; non-numeric → null. */
const decode = (raw: string): number | null => {
  const stripped = raw.replace(/,/g, '').trim();
  if (stripped === '') return null;
  const n = Number(stripped);
  return Number.isNaN(n) ? null : n;
};

const SalesNumberInput = (props: SalesNumberInputProps) => {
  const synced = display(props.value, props.formatter);
  const [draft, setDraft] = useState(synced);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(synced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced]);

  const decoded = decode(draft);
  const invalid =
    decoded !== null &&
    ((props.min != null && decoded < props.min) || (props.max != null && decoded > props.max));

  const onBlur = () => {
    if (draft === synced) return;
    const v = decode(draft);
    // Reformat the display (formatter onblur setValue parity).
    setDraft(display(v, props.formatter));
    props.onCommit?.(props.name, v);
  };

  return (
    <input
      ref={ref}
      autoComplete="off"
      type="text"
      className={`${props.className}${invalid ? ' invalid' : ''}`}
      name={props.name}
      value={draft}
      placeholder={props.placeholder}
      readOnly={props.readOnly}
      tabIndex={props.readOnly ? -1 : undefined}
      disabled={props.disabled}
      data-formatter_type={props.formatter}
      data-min={props.min}
      data-max={props.max}
      data-step={props.step}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={props.readOnly ? undefined : onBlur}
    />
  );
};

export default SalesNumberInput;
